import React from "react";
import { Navigate, Route, Routes } from "react-router";
import { useAuthorization } from "@common/providers";
import { useWhoAmIQuery } from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { usePsaIntegrations } from "./api/usePsaIntegrations";
import { usePsaProjectRecord } from "./api/usePsaProjectRecord";
import IntegrationPicker from "./IntegrationPicker/IntegrationPicker";
import New from "./New/New";
import Edit from "./Edit/Edit";
import { ResolvedIntegration } from "./types/PsaProject";

interface Props {
  project_id: string;
  account_slug: string;
}

const PsaProjectMspa: React.FC<Props> = ({ project_id }) => {
  const projectId = project_id;

  const { data: currentUser, isLoading: currentUserLoading } = useWhoAmIQuery();
  const { integrations, isLoading: integrationsLoading } = usePsaIntegrations();
  const {
    project,
    record,
    isLoading: recordLoading,
  } = usePsaProjectRecord(projectId);
  const [chosen, setChosen] = React.useState<ResolvedIntegration | null>(null);
  const { authorized } = useAuthorization() || {};

  if (
    !authorized ||
    currentUserLoading ||
    integrationsLoading ||
    recordLoading ||
    !project ||
    !currentUser
  ) {
    return <ScopeStackSpinner />;
  }

  const recordConnectionId = (record as any)?.relationships?.[
    "account-connection"
  ]?.data?.id;
  const recordIntegration = recordConnectionId
    ? integrations.find(
        (i) => i.accountConnectionId === String(recordConnectionId)
      ) ?? null
    : null;
  const active =
    recordIntegration ??
    chosen ??
    (integrations.length === 1 ? integrations[0] : null);

  if (integrations.length === 0) {
    return (
      <div data-testid="psa-none">
        No PSA integration is configured for this account.
      </div>
    );
  }

  if (!active) {
    return (
      <IntegrationPicker integrations={integrations} onSelect={setChosen} />
    );
  }

  const privileges = currentUser?.data?.attributes?.privileges || [];
  const psaAccessLevel = privileges.find(
    (p) => p.privilege === "projects.psa"
  )?.["access-level"];
  const canManage = psaAccessLevel === "create" || psaAccessLevel === "manage";

  return (
    <Routes>
      <Route
        path="/new"
        element={
          record ? (
            <Navigate to={`/projects/${projectId}/psa_project/edit`} replace />
          ) : (
            <New
              projectId={projectId}
              integration={active}
              canManage={canManage}
            />
          )
        }
      />
      <Route
        path="/edit"
        element={
          record ? (
            <Edit
              projectId={projectId}
              integration={active}
              record={record}
              canManage={canManage}
            />
          ) : (
            <Navigate to={`/projects/${projectId}/psa_project/new`} replace />
          )
        }
      />
      <Route
        path="/*"
        element={
          <Navigate to={`/projects/${projectId}/psa_project/new`} replace />
        }
      />
    </Routes>
  );
};

export default PsaProjectMspa;
